const DAY = 24 * 60 * 60 * 1000;

/** The time until which the pop-up stays quiet, kept in the browser of the visitor (nothing is sent anywhere). */
function readQuietUntil(key) {
  try {
    return Number(localStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
}

function writeQuietUntil(key, time) {
  try {
    localStorage.setItem(key, String(time));
  } catch {
    // Storage blocked: the pop-up may come back on the next page, which is the lesser evil.
  }
}

/**
 * <newsletter-popup> — opens its <dialog> a few seconds after the page is ready, once per "quiet period".
 *
 *  - closed by the visitor: quiet for data-days days;
 *  - signed up: quiet for a year. The sign-up form is the platform's own: it brings the visitor back to the page, with
 *    the result in the form. The pop-up then opens at once to show it (the thank-you, or what is wrong), instead of
 *    leaving a visitor who has just signed up with nothing to read;
 *  - never over another dialog (cart, quick add, search): it waits and tries once more.
 *
 * In the theme editor it opens when its section is selected and closes when it is not.
 */
class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.key = `nt:popup:${this.dataset.key}`;
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('close', this.onClose);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.onEditorDeselect);
      return;
    }

    // The page came back from the platform with the result of the form of the pop-up: show it.
    if (this.querySelector('.fmsg')) {
      if (this.querySelector('.fmsg--success')) this.signedUp();
      return this.openWhenFree();
    }

    if (this.hasAttribute('data-desktop-only') && matchMedia('(max-width: 749px)').matches) return;
    if (readQuietUntil(this.key) > Date.now()) return;

    this.timer = setTimeout(() => this.openWhenFree(), Number(this.dataset.delay) * 1000);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('shopify:section:select', this.onEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
  }

  /** Another dialog is open (the visitor is in the cart, say): try again a little later, once. */
  openWhenFree(retried = false) {
    if (this.dialog.open) return;
    if (document.querySelector('dialog[open]')) {
      if (!retried) this.timer = setTimeout(() => this.openWhenFree(true), 8000);
      return;
    }
    this.dialog.showModal();
    // Without a message to read (autofocus), the dialog itself takes the focus: its name is read out, no keyboard pops up.
    if (!this.dialog.querySelector('[autofocus]')) this.dialog.focus();
  }

  onDialogClick = (event) => {
    // A click on the backdrop lands on the dialog element itself.
    if (event.target === this.dialog || event.target.closest('[data-close]')) this.dialog.close();
  };

  onClose = () => {
    if (!window.Shopify?.designMode && !this.subscribed) {
      writeQuietUntil(this.key, Date.now() + Number(this.dataset.days) * DAY);
    }
  };

  signedUp() {
    this.subscribed = true;
    writeQuietUntil(this.key, Date.now() + 365 * DAY);
  }

  onEditorSelect = (event) => {
    if (event.target.contains(this) && !this.dialog.open) this.dialog.showModal();
  };

  onEditorDeselect = (event) => {
    if (event.target.contains(this) && this.dialog.open) this.dialog.close();
  };
}

if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopup);
